import React, { useState, useEffect } from 'react';
import {
  Info, X, Check, ArrowRight, CalendarBlank, ClockCounterClockwise, ArrowClockwise,
} from '@phosphor-icons/react';
import {
  getBranchNames,
  getGroupCode,
  getReauctionSubscriberDetails,
  getReauctionDetails,
  getReauctionDividendDetails,
  updateReauctionTransactionDates,
  saveData,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { pickerOnly } from '../utils/pickerOnly';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

// Mirrors Auth.buildAudit(changeType, { oldData, newData }, reason) from the Angular app.
function buildAudit(schemaName, loginName, changeType, { oldData, newData }, reason) {
  return {
    schemaName,
    loginName,
    loginTime: new Date().toISOString(),
    changeType,
    oldData,
    newData,
    reason,
  };
}

// reauction_date arrives as whatever the API host's culture rendered the timestamp
// as — an ISO string on most servers, dd-MM-yyyy on a non-invariant one — and always
// carries a time part the screen must not show. Reading the components off the string
// also avoids new Date('yyyy-MM-dd') being taken as UTC midnight, which resolves to
// the previous day in any timezone west of Greenwich.
function parseApiDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

  const dmy = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));

  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// p-datepicker binds a Date object to the form control; the backend/payload
// still expects the same 'yyyy-MM-dd' string the native <input type="date"> used to send.
function formatDate(value) {
  const d = parseApiDate(value);
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** dd-MM-yyyy, date only — matches how the date inputs beside it render. */
function toDisplayDate(value) {
  const d = parseApiDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function buildQuotedList(list) {
  return list.map((v) => `'${v}'`).join(',');
}

function ReAuctionDateForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [reauctionRows, setReauctionRows] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [subscriberName, setSubscriberName] = useState('');
  const [reAuctionDate, setReAuctionDate] = useState('');
  const [newReAuctionDate, setNewReAuctionDate] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const [branchschema, setBranchschema] = useState(null);
  const [branchCode, setBranchCode] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);

  const [contactId, setContactId] = useState(null);
  const [subscriberjvno, setSubscriberjvno] = useState(null);
  const [, setFromTicketNo] = useState(null);
  const [toTicketNo, setToTicketNo] = useState(null);
  const [oldReauctionDate, setOldReauctionDate] = useState(null);
  const [bidjvTransactionNos, setBidjvTransactionNos] = useState([]);
  const [dividendTransactionNos, setDividendTransactionNos] = useState([]);

  const [minReAuctionDate, setMinReAuctionDate] = useState(null);
  const [maxReAuctionDate, setMaxReAuctionDate] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pendingOldDate, setPendingOldDate] = useState('');
  const [pendingNewDate, setPendingNewDate] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromTicket = () => {
    setContactId(null);
    setSubscriberjvno(null);
    setFromTicketNo(null);
    setToTicketNo(null);
    setOldReauctionDate(null);
    setBidjvTransactionNos([]);
    setDividendTransactionNos([]);
    setMinReAuctionDate(null);
    setMaxReAuctionDate(null);
    setSubscriberName('');
    setReAuctionDate('');
    setNewReAuctionDate('');
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setReauctionRows([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setBranchCode(null);
    setGroup('');
    setBranchCodeList([]);
    resetFromGroup();
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    resetFromBranch();
    if (!branchName) return;

    const selected = branchlist.find((x) => x.branch_name === branchName);
    if (!selected) return;

    setBranchschema(selected.branch_code);
    setBranchCode(selected.branch_code);

    try {
      const res = await getGroupCode(selected.branch_code);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const handleGroupChange = async (groupCodeValue) => {
    setGroup(groupCodeValue);
    resetFromGroup();
    if (!groupCodeValue) return;

    const selected = branchCodeList.find((x) => x.group_Code === groupCodeValue);
    if (!selected) return;

    setGroupCode(selected.group_Code);
    setChitgroupId(selected.chitgroup_id);

    try {
      // Only tickets that actually have a reauction entry can be changed here, so the
      // From Ticket No list comes from the reauction rows rather than the group's full
      // ticket list — and those same rows already carry every detail the screen shows.
      const res = await getReauctionSubscriberDetails(branchschema, selected.group_Code, null, branchCode);
      // A ticket with more than one reauction entry comes back as several rows.
      // Keeping the first of each mirrors the res[0] the per-ticket call used to
      // resolve to, and keeps the dropdown from listing the same ticket twice.
      const seen = new Set();
      setReauctionRows((Array.isArray(res) ? res : []).filter((r) => {
        const key = String(r.from_ticketno);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }));
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setReauctionRows([]);
    }
  };

  const handleTicketChange = (fromTicket) => {
    setTicket(fromTicket);
    resetFromTicket();
    if (!fromTicket) return;

    const d = reauctionRows.find((r) => String(r.from_ticketno) === String(fromTicket));
    if (!d) {
      showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      return;
    }

    setContactId(d.tbl_mst_contact_id);
    setSubscriberjvno(d.transaction_no);
    setFromTicketNo(d.from_ticketno);
    setToTicketNo(d.to_ticketno);
    // Kept raw: the update's WHERE clauses match transaction_date against it verbatim.
    setOldReauctionDate(d.reauction_date);
    setSubscriberName(d.subscriber_name ?? '');
    setReAuctionDate(toDisplayDate(d.reauction_date));

    // ── set the allowed date window: [oldReauctionDate, oldReauctionDate + 6 months] ──
    const oldDate = parseApiDate(d.reauction_date);
    if (oldDate) {
      setMinReAuctionDate(oldDate);
      const max = new Date(oldDate);
      max.setMonth(max.getMonth() + 6);
      setMaxReAuctionDate(max);
    } else {
      setMinReAuctionDate(null);
      setMaxReAuctionDate(null);
    }

    // The picker opens on the current reauction date rather than an empty
    // dd-mm-yyyy placeholder, so the field always shows the date being moved.
    setNewReAuctionDate(oldDate ? formatDate(oldDate) : '');

    loadReauctionDetails(d.from_ticketno, d.to_ticketno);
    loadDividendDetails(d.transaction_no);
  };

  const loadReauctionDetails = async (from, to) => {
    try {
      const res = await getReauctionDetails(branchschema, from, to);
      const unique = (res || [])
        .map((x) => x.bidjv_transaction_no)
        .filter((v, i, arr) => v && arr.indexOf(v) === i);
      setBidjvTransactionNos(unique);
    } catch {
      setBidjvTransactionNos([]);
    }
  };

  const loadDividendDetails = async (jvno) => {
    try {
      const res = await getReauctionDividendDetails(branchschema, jvno);
      const unique = (res || [])
        .map((x) => x.dividend_transaction_number)
        .filter((v, i, arr) => v && arr.indexOf(v) === i);
      setDividendTransactionNos(unique);
    } catch {
      setDividendTransactionNos([]);
    }
  };

  /**
   * New reauction date must be:
   *  - on or after the current (old) reauction date, and
   *  - no more than 6 months after it.
   */
  const getDateError = () => {
    if (!newReAuctionDate) return 'required';
    const val = parseApiDate(newReAuctionDate);
    if (!val) return null;

    if (minReAuctionDate && startOfDay(val) < startOfDay(minReAuctionDate)) return 'tooEarly';
    if (maxReAuctionDate && startOfDay(val) > startOfDay(maxReAuctionDate)) return 'tooLate';
    return null;
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const dateError = touched ? getDateError() : null;
  const dateInvalid = !!dateError;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!cao && !!group && !!ticket && !getDateError()
    && reason.trim().length >= 10 && reason.length <= 200;

  // ── Save flow ───────────────────────────────────────────────────
  // Step 1: validate + stage data, open confirm modal
  const save = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (!contactId || !subscriberjvno) {
      showToast('warn', 'Subscriber Not Loaded', 'Could not resolve subscriber details for the selected ticket. Please re-select.');
      return;
    }

    if (!Number.isFinite(Number(chitgroupId)) || !Number.isFinite(Number(toTicketNo))) {
      showToast('warn', 'Ticket Not Loaded', 'Chit group / to-ticket details did not load for this ticket. Please re-select the group and ticket.');
      return;
    }

    const formattedNewDate = formatDate(newReAuctionDate);

    setPendingOldDate(oldReauctionDate);
    setPendingNewDate(formattedNewDate);
    setPendingReason(reason);

    setPendingPayload({
      branchschema,
      chitgroupid: chitgroupId,
      toticketno: toTicketNo,
      ticketno: Number(ticket),
      reauctiondate: formattedNewDate,
      // yyyy-MM-dd, date only. The raw value carries a time and, on some API hosts, a
      // dd-MM-yyyy rendering, which the update's date matches could not parse — so the
      // rows were never found. The backend now compares ::date and keeps each row's time.
      oldtransactiondate: formatDate(oldReauctionDate),
      createddate: new Date().toISOString(),
      subscriberjvno,
      dividendtransactionnos: buildQuotedList(dividendTransactionNos),
      bidjvtransactionnos: buildQuotedList(bidjvTransactionNos),
    });

    setPendingAudit(buildAudit(
      branchschema,
      loginName,
      'REAUCTION_DATE_CHANGE',
      {
        oldData: {
          branchCode,
          groupCode,
          ticketNo: ticket,
          subscriberName,
          reAuctionDate: toDisplayDate(oldReauctionDate),
        },
        newData: {
          newReAuctionDate: toDisplayDate(formattedNewDate),
        },
      },
      reason,
    ));

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingAudit(null);
    setPendingPayload(null);
  };

  // Step 2: fires only after user clicks "Yes, Save" in the modal
  const confirmSave = async () => {
    if (!pendingAudit || !pendingPayload) return;

    setSaving(true);
    try {
      // UpdateReauctionTransactionDates swallows its own SQL errors and returns
      // the message body with HTTP 200 — "Success" is the only real success.
      const result = await updateReauctionTransactionDates(pendingPayload);
      const text = typeof result === 'string' ? result : JSON.stringify(result ?? '');
      if (!text.toLowerCase().includes('success')) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Reauction update reported a failure:', text);
        showToast('error', 'Update Failed', text || 'Could not update the reauction date. Please try again.', true);
        return;
      }
      try {
        await saveData([pendingAudit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Reauction date updated and audit saved successfully.', true);
        handleClear();
      } catch (err) {
        console.error('Audit save failed:', err);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('warn', 'Partial Success', 'Reauction date updated, but audit log failed to save.', true);
        handleClear();
      }
    } catch (err) {
      console.error('Update failed:', err);
      setSaving(false);
      setShowConfirmModal(false);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not update the reauction date. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setPendingAudit(null);
    setPendingPayload(null);
  };

  const minDateStr = minReAuctionDate ? formatDate(minReAuctionDate) : undefined;
  const maxDateStr = maxReAuctionDate ? formatDate(maxReAuctionDate) : undefined;

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="reauction-date-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="reauction-date-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CalendarBlank size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>ReAuction Date Change</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group and ticket to load reauction details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="reauction-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="reauction-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              From Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={reauctionRows}
              getLabel={(o) => String(o.from_ticketno)}
              getValue={(o) => o.from_ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select from ticket no"
              emptyMessage={groupCode ? 'No reauctioned tickets in this group' : 'Select a group code first'}
              disabled={!groupCode}
              error={ticketInvalid}
              testId="reauction-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>From Ticket No is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              To Ticket No
            </label>
            <input
              className="input-field"
              style={{ fontSize: '13px', background: '#F9FAFB' }}
              value={toTicketNo ?? ''}
              readOnly
              placeholder="Select a from ticket no"
              data-testid="reauction-to-ticket"
            />
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!ticket} hint="Select a ticket in section A first">
        {/* SECTION B: Current Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Current Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Read-only details loaded from the selected ticket</div>
          </div>
        </div>

        <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', background: '#F9FAFB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '10px' }}>
            <ClockCounterClockwise size={13} /> Current Details
            <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>READ ONLY</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Subscriber Name</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={subscriberName} readOnly data-testid="reauction-subscriber-name" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>ReAuction Date</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={reAuctionDate} readOnly data-testid="reauction-old-date" />
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!ticket} hint="Select a ticket in section A first">
        {/* SECTION C: New ReAuction Date */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>New ReAuction Date</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Must be on/after the current reauction date, and within 6 months of it</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              New ReAuction Date <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                className="input-field st-datepicker"
                style={{ fontSize: '13px', borderColor: dateInvalid ? '#DC2626' : undefined }}
                value={toDisplayDate(newReAuctionDate)}
                placeholder="DD-MM-YYYY"
                disabled={!ticket}
                readOnly
                tabIndex={-1}
                data-testid="reauction-new-date-display"
              />
              {/* A native date input renders in the browser's locale; this one only
                  provides the picker, over the DD-MM-YYYY text above. */}
              <input
                type="date"
                aria-label="New ReAuction Date"
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  opacity: 0, cursor: ticket ? 'pointer' : 'not-allowed',
                }}
                value={newReAuctionDate}
                min={minDateStr}
                max={maxDateStr}
                disabled={!ticket}
                onChange={(e) => setNewReAuctionDate(e.target.value)}
                {...pickerOnly({ disabled: !ticket })}
                data-testid="reauction-new-date"
              />
            </div>
            {dateError === 'required' && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New ReAuction Date is required</div>}
            {dateError === 'tooEarly' && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New date cannot be earlier than the current reauction date ({reAuctionDate})</div>}
            {dateError === 'tooLate' && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New date cannot be more than 6 months after the current reauction date</div>}
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!ticket || !newReAuctionDate} hint="Select a ticket and enter the new reauction date first">
        {/* SECTION D: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>D</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Explain why this reauction date is being changed..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="reauction-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : 'Reason must be at least 10 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="reauction-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={save} className="btn-primary" disabled={saving} data-testid="reauction-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="reauction-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CalendarBlank size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm ReAuction Date Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You're about to update the reauction date for this ticket. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old ReAuction Date</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{toDisplayDate(pendingOldDate) || '—'}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New ReAuction Date</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{toDisplayDate(pendingNewDate)}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="reauction-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="reauction-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReAuctionDateForm;